import React from 'react';
import { NavLinkProps } from 'react-router-dom';
import { ClearingWithdrawInstrumentType, Money } from '../types/gql';
import ToPayeer from '../instruments/ToPayeer';
import ToAdvCash from '../instruments/ToAdvCash';
import ToUTT from '../instruments/ToUTT';
import ToCard from '../instruments/ToCard';
import ToTinkoffCard from '../instruments/ToTinkoffCard';
import ToAlfaBankAccount from '../instruments/ToAlfaBankAccount';
import ToBank from '../instruments/ToBank';
import { EnabledInstrument } from './useInstruments.types';
import { InstrumentCommission } from '../types/instrument';

export interface Instrument {
  id: string | null;
  type: ClearingWithdrawInstrumentType;
  currency: string;
  commission: InstrumentCommission;
  minimum: Money;
  maximum: Money;
}

interface Props {
  instrument: Instrument;
}

export interface AvailableInstrument {
  MenuItem: React.FunctionComponent<Props & NavLinkProps>;
  SelectItem: React.FunctionComponent<Props & React.ButtonHTMLAttributes<HTMLButtonElement>>;
  Content: React.FunctionComponent<Instrument>;
  instrument: Instrument;
  pathname: string;
}

export enum WellKnownInstruments {
  TINKOFF = '10',
  PAYEER = '12',
  ADVCASH = '13',
}

export const mapInstruments = (instruments: EnabledInstrument[]) =>
  instruments
    .map<Instrument>(({ instrumentId, fee, maximum, minimum, ...rest }) => ({
      ...rest,
      commission: fee,
      id: instrumentId || null,
      minimum: { value: minimum || 0, currency: rest.currency },
      maximum: { value: maximum || Number.MAX_SAFE_INTEGER, currency: rest.currency },
    }))
    .map<AvailableInstrument | null>((instrument) => {
      switch (instrument.type) {
        case ClearingWithdrawInstrumentType.ProviderWallet: {
          switch (instrument.id) {
            case WellKnownInstruments.PAYEER:
            return {
              ...ToPayeer,
              instrument,
              pathname: 'to-payeer',
            };
            case WellKnownInstruments.ADVCASH: {
              return {
                ...ToAdvCash,
                instrument,
                pathname: 'to-advcash',
              }
            }
            default:
              return null;
          }
        }

        case ClearingWithdrawInstrumentType.BankTransfer:
          return {
            ...ToBank,
            instrument,
            pathname: `to-bank-${instrument.currency}`,
          };

        case ClearingWithdrawInstrumentType.AlfaBankAccount:
          return {
            ...ToAlfaBankAccount,
            instrument,
            pathname: 'to-alfa-bank',
          };

        case ClearingWithdrawInstrumentType.Card: {
          if (instrument.id === WellKnownInstruments.TINKOFF) {
            return {
              ...ToTinkoffCard,
              instrument,
              pathname: 'to-tinkoff-card',
            };
          }
          return {
            ...ToCard,
            instrument,
            pathname: `to-card-${instrument.currency}-${instrument.id}`,
          };
        }

        case ClearingWithdrawInstrumentType.Crypto:
          return {
            ...ToUTT,
            instrument,
            pathname: 'to-utt',
          };
      }
    }).filter(Boolean) as AvailableInstrument[];
